/**
 ____  _____    _    ____    _____ _   _ ___ ____
|  _ \| ____|  / \  |  _ \  |_   _| | | |_ _/ ___|
| |_) |  _|   / _ \ | | | |   | | | |_| || |\___ \
|  _ <| |___ / ___ \| |_| |   | | |  _  || | ___) |
|_| \_\_____/_/   \_\____/    |_| |_| |_|___|____/

   This file is unused merging it to the DashboardV2.js
   remove later?!

*/
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Inertia } from "@inertiajs/inertia";
import AppLayout from "@/Layouts/AppLayout";
import { Container, DateSelectSpan } from "@/Components";
import QuestionAnswers from "@/Components/Dashboard/QuestionAnswers";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  Select,
  LabeledSelect,
} from "@/Components/Form";

import { PlusSquare, MinusSquare, Search, Bookmark } from "react-feather";

import { useAtom } from "jotai";
import {
  teamFilterAtom,
  questionFilterAtom,
  dateFilterAtom,
  locationFilterAtom,
  generalFilterAtom,
} from "@/Stores";

import { IPCC_FORMAT } from "@/Enums/ipcc_format";
import DashboardGraphs from "@/Components/Dashboard/DashboardGraphs";
import { usePage } from "@inertiajs/inertia-react";
import _ from "lodash";
import { LoadingProvider, useLoading } from "@/Contexts/LoadingContext";
import { formatDate } from "@/Utilities/Utilities";
import { endOfMonth, startOfMonth, subMonths } from "date-fns";

function QuestionsList() {
  const teams = usePage().props?.teams?.data || [];

  const [search, setSearch] = useState("");
  const [filterTags, setFilterTags] = useState([]);

  const questionCounts = useMemo(
    () =>
      // Flatten the questions from all teams into a single array
      teams
        ?.flatMap((team) => team?.questions)
        .reduce((counts, question) => {
          const existing = counts.find(
            (count) => count.question.id === question.id,
          );
          existing ? existing.count++ : counts.push({ question, count: 1 });
          return counts;
        }, []),
    [teams],
  );

  // Get all tags from questions but make sure they are unique by name
  const tags = useMemo(
    () =>
      _.uniqBy(
        questionCounts.flatMap(({ question }) => question.tags),
        "name",
      ),
    [questionCounts],
  );

  return (
    <>
      <div className="flex justify-between">
        <LabeledMultiSelectAutocomplete
          className="w-64 text-gray-700"
          options={tags.map((tag) => ({ value: tag.name, label: tag.name }))}
          initialChecked={true}
          placeholder={`All tags`}
          icon={<Bookmark />}
          onSelect={(val) => setFilterTags(val.map((v) => v.value))}
        />

        <LabeledInput
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          placeholder="Search activity"
          className="pr-10"
          containerClass="w-96"
          unit={<Search className="relative -top-1" />}
        />
      </div>

      <div>
        {questionCounts.map(({ question, count }) => {
          if (
            search &&
            !question.title.toLowerCase().includes(search.toLowerCase())
          ) {
            return null;
          }

          if (
            question.tags.length > 0 &&
            filterTags.length > 0 &&
            !filterTags.some((tag) => question.tags.some((t) => t.name === tag))
          ) {
            return null;
          }

          return (
            <QuestionAnswers
              key={question.id}
              question={question}
              count={count}
              countTeam={teams.length}
            />
          );
        })}
      </div>
    </>
  );
}

export default function Dashboard() {
  const teams = usePage().props?.teams?.data || [];

  return (
    <LoadingProvider>
      <Container
        size={"4xl:max-w-screen-3xl"}
        padding={"px-4 lg:px-16 4xl:pl-32"}
        className="sm:dots-orange mt-4 4xl:mx-0"
      >
        <div className="flex flex-col gap-4 p-4">
          <h2 className="text-5xl font-bold">Results</h2>

          <DashboardFilter />

          <h2 className="mt-4 border-t border-[#C4C4C4] pt-6 text-2xl font-bold">
            Total emissions
          </h2>

          <DashboardGraphs />

          <h2 className="mt-4 text-2xl font-bold">Your activities</h2>

          <QuestionsList />
        </div>
      </Container>
    </LoadingProvider>
  );
}

Dashboard.layout = (page) => <AppLayout title="Results">{page}</AppLayout>;

const DashboardFilter = () => {
  const { setIsLoaded } = useLoading();
  const initialLoad = useRef(true);

  const teams = usePage().props?.teams?.data || [];
  const questions = usePage().props?.questions?.data || [];
  const locations =
    usePage().props?.locations?.map((location) => ({
      value: location?.location_place_id || "",
      label: location?.location || "",
    })) || [];

  const [teamFilter, setTeamFilter] = useAtom(teamFilterAtom);
  const [questionFilter, setQuestionFilter] = useAtom(questionFilterAtom);
  const [dateFilter, setDateFilter] = useAtom(dateFilterAtom);
  const [locationFilter, setLocationFilter] = useAtom(locationFilterAtom);
  const [generalFilter, setGeneralFilter] = useAtom(generalFilterAtom);

  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [filtersDirty, setFiltersDirty] = useState(false);

  const [tempFilters, setTempFilters] = useState({
    teamFilter: [],
    questionFilter: [],
    dateFilter: null,
    locationFilter: [],
    generalFilter: {},
    initialLoad: 0,
  });

  useEffect(() => {
    // console.log('useeffect atoms updated', {teamFilter, questionFilter, dateFilter, locationFilter, generalFilter});
    if (initialLoad.current === true) {
      // console.log('initial load');
      initialLoad.current = false;
      setTempFilters({
        teamFilter: teamFilter,
        questionFilter: questionFilter,
        dateFilter: dateFilter,
        locationFilter: locationFilter,
        generalFilter: generalFilter,
        initialLoad: 1,
      });
    }
  }, [teamFilter, questionFilter, dateFilter, locationFilter, generalFilter]);

  useEffect(() => {
    if (tempFilters.initialLoad === 1) {
      // This function will only run once, when the page is loaded and the atom is added to the tempFilters state as initialLoad will only be 1 at that time
      updateFilters();
      setTempFilters((prev) => ({ ...prev, initialLoad: 2 }));
    }
    console.log("tempFilters changed", tempFilters);
  }, [tempFilters]);

  const updateFilters = () => {
    const filters = tempFilters;
    const payload = {
      teamFilter: filters.teamFilter,
      questionFilter: filters.questionFilter,
      locationFilter: filters.locationFilter,
      generalFilter: filters.generalFilter,
      dateFilter: {},
    };

    Object.keys(filters.dateFilter).forEach((key) => {
      // Make sure the dates is formatted correctly and is in the swedish timezone
      payload.dateFilter[key] = formatDate(
        new Date(filters.dateFilter[key]).toISOString(),
        "yyyy-MM-dd",
      );
    });

    Inertia.put(route("reporter.dashboard.post"), payload, {
      preserveScroll: true,
      onSuccess: (page) => {
        setTeamFilter(filters.teamFilter);
        setQuestionFilter(filters.questionFilter);
        setDateFilter(filters.dateFilter);
        setLocationFilter(filters.locationFilter);
        setGeneralFilter(filters.generalFilter);
        setFiltersDirty(false);
        setIsLoaded(true);
      },
    });
  };

  function handleChangeFilter(val, filter) {
    setFiltersDirty(true);
    setTempFilters((oldFilters) => {
      const updatedFilters = { ...oldFilters };
      switch (filter) {
        case "team":
          updatedFilters.teamFilter = val.map((v) => v.value);
          break;
        case "question":
          updatedFilters.questionFilter = val.map((v) => v.value);
          break;
        case "date":
          updatedFilters.dateFilter = val;
          break;
        case "location":
          updatedFilters.locationFilter = val.map((v) => v.value);
          break;
        default:
          console.log(
            filter,
            Array.isArray(val)
              ? val.map((v) => v.value)
              : val?.target?.value || null,
          );

          updatedFilters.generalFilter = {
            ...oldFilters.generalFilter,
            [filter]: Array.isArray(val)
              ? val.map((v) => v.value)
              : val?.target?.value || null,
          };
          break;
      }
      return updatedFilters;
    });
  }

  return (
    <>
      <div className="flex justify-start gap-2 align-middle">
        <label className="mr-4">
          <input
            type="radio"
            name="reports"
            onChange={(e) => handleChangeFilter(e, "reports")}
            value="all"
            checked={(tempFilters.generalFilter?.reports || "all") === "all"}
          />
          <span className="ml-2">All reports</span>
        </label>
        <label className="mr-4">
          <input
            type="radio"
            name="reports"
            onChange={(e) => handleChangeFilter(e, "reports")}
            value="my_reports"
            checked={
              (tempFilters.generalFilter?.reports || "all") === "my_reports"
            }
          />
          <span className="ml-2">My reports</span>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2 align-middle">
        <LabeledMultiSelectAutocomplete
          title="Team"
          className="mb-4 text-gray-700"
          options={teams.map((team) => ({ value: team.id, label: team.name }))}
          initialChecked={true}
          defaultValue={teamFilter}
          placeholder={`Teams`}
          onSelect={(val) => handleChangeFilter(val, "team")}
        />

        <DateSelectSpan
          label="Reporting period"
          className="mb-4 mt-0 min-h-[46px] w-full rounded-none px-3 py-2 text-gray-700 shadow-sm"
          value={dateFilter}
          buttons={["last12months", "month", "year", "lastMonth", "lastYear"]}
          onChange={(val) => handleChangeFilter(val, "date")}
        />

        <LabeledMultiSelectAutocomplete
          title="Activities"
          className="mb-4 text-gray-700"
          options={questions
            .map((question) => ({ value: question.id, label: question.title }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          initialChecked={true}
          defaultValue={questionFilter}
          placeholder={`Activities`}
          onSelect={(val) => handleChangeFilter(val, "question")}
        />
      </div>
      {/* )} */}

      {showAdditionalFilters && (
        <div className="grid grid-cols-3 gap-2 align-middle">
          <LabeledMultiSelectAutocomplete
            title="Data source"
            initialChecked={true}
            options={[
              { label: "Reporters", value: "reporters" },
              { label: "Calculators", value: "calculators" },
              { label: "Webhooks", value: "webhooks" },
            ]}
            defaultValue={tempFilters.generalFilter?.source || []}
            onSelect={(val) => handleChangeFilter(val, "source")}
            placeholder={`Sources`}
          />

          <LabeledMultiSelectAutocomplete
            title="Location"
            initialChecked={true}
            options={locations}
            defaultValue={tempFilters.locationFilter}
            onSelect={(val) => handleChangeFilter(val, "location")}
            placeholder={`Locations`}
          />

          <LabeledSelect
            title="IPCC report format"
            options={IPCC_FORMAT}
            defaultValue={tempFilters.generalFilter?.format || "ar5"}
            onChange={(val) => handleChangeFilter(val, "format")}
            placeholder={`Formats`}
            noDefault={true}
          />
        </div>
      )}

      <div className="flex flex-row items-center justify-between">
        <button
          className="flex flex-row gap-1"
          onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
        >
          {showAdditionalFilters ? "Less choices" : "More choices"}{" "}
          {showAdditionalFilters ? <MinusSquare /> : <PlusSquare />}
        </button>
        <div>
          {/* <button className="p-3 mr-2 text-white bg-primary disabled:bg-gray-400" onClick={resetFilters}>Reset filters</button> */}
          <button
            className="bg-primary p-3 text-white disabled:bg-gray-400"
            disabled={!filtersDirty}
            onClick={() => updateFilters()}
          >
            Apply filters
          </button>
        </div>
      </div>
    </>
  );
};
